import cn from 'classnames';
import { RefObject, useMemo } from 'react';
import type { VirtualItem } from '@tanstack/react-virtual';

import type { BaseRowData, LegacyDataProvider } from '@adv-frontend/data-provider';
import { HStack, useHStack, usePress } from '@direct-frontend/components';

import type { GridRow, GridTable, GridCell, GridVisualOptions } from '../../../types';
import type { GridRowSelectionManager } from '../../../lib/selectionManager/selectionManager';
import { getRowIndexes } from '../../../lib/getRowIndexes/getRowIndexes';

import { Cell } from '../Cell/Cell';
import { CellStub } from '../Cell/CellStub';
import { RowMeasurer } from '../RowMeasurer/RowMeasurer';

import styles from '../Grid.module.css';

/**
 * Пропсы компонента `Row`.
 *
 * @typeParam RowData Данные грида.
 */
export type RowProps<RowData extends BaseRowData> = {
    /**
     * Внешний атрибут className
     */
    className?: string;
    /**
     * Датапровайдер.
     */
    dataProvider: LegacyDataProvider<RowData>;
    /**
     * Инстанс грида.
     */
    grid: GridTable<RowData>;
    /**
     * Инстанс строки.
     */
    row: GridRow<RowData>;
    /**
     * Параметры выделения строк.
     */
    rowHighlight?: GridVisualOptions['rowHighlight'];
    /**
     * Виртуальная строка.
     */
    virtualRow: VirtualItem;
    /**
     * Виртуальные столбцы.
     */
    virtualColumns: VirtualItem[];
    /**
     * Функция пересчета виртуализации при изменении размеров строки
     */
    measure: (node: HTMLDivElement | null) => void;
    /**
     * Менеджер выделения строк.
     */
    selectionManager: GridRowSelectionManager | undefined;
    /**
     * Ссылка на скролящуюся часть грида.
     */
    scrollableRef: RefObject<HTMLDivElement>;
    /**
     * Фиксированная высота строки.
     */
    strictHeight?: number;
    /**
     * Скрывать ли нижний бордер у строки
     */
    hideBottomBorder: boolean;
    /**
     * Коллбек клика по строке
     */
    onClick?: (rowData: RowData) => void;
};

// Тип виртуализированной ячейки
interface VirtualizedCell<RowData extends BaseRowData> extends GridCell<RowData> {
    offsetLeft?: number;
}

/**
 * Компонент строки грида.
 *
 * @typeParam RowData Данные грида.
 **/
export function Row<RowData extends BaseRowData>({
    className,
    dataProvider,
    grid,
    row,
    rowHighlight,
    virtualRow,
    virtualColumns,
    measure,
    selectionManager,
    scrollableRef,
    strictHeight,
    hideBottomBorder,
    onClick,
}: RowProps<RowData>) {
    const { byHover = true, bySelect = true } = rowHighlight ?? {};
    const { data, isFetching, isLoading } = dataProvider.useRowData(getRowIndexes(row));
    const isSelected = selectionManager?.useIsSelected(row.id);
    const isRowLoading = isFetching || isLoading;
    // Строка загрузки не должна мерить сама себя: RowMeasurer через ResizeObserver
    // зафиксировал бы первую попавшуюся высоту навсегда, поэтому скелетон рисуется
    // с явной высотой виртуализатора, как строка с strictHeight
    const rowHeight = strictHeight ?? (isRowLoading ? virtualRow.size : undefined);
    const inlineStyles = useMemo(() => ({ top: virtualRow.start, height: rowHeight }), [virtualRow.start, rowHeight]);
    const stackClasses = useHStack({});
    const pixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio : 1;
    const bottomBorderSize = hideBottomBorder ? 0 : Math.max(Math.round(1 / pixelRatio), 1);

    const renderCells = (cells: VirtualizedCell<RowData>[]) => {
        const cName = cn(styles.rowHightlighter, className, hideBottomBorder ? null : styles.withBorder);
        return (
            <HStack className={cName}>
                {cells.map(cell => {
                    if (isRowLoading) {
                        return (
                            /**
                             * Высота строки включает нижнюю границу родительского контейнера.
                             * Вычитаем её толщину с поправкой на zoom только когда граница видима.
                             */
                            <CellStub key={cell.id} cell={cell} height={virtualRow.size - bottomBorderSize} />
                        );
                    }

                    return (
                        <Cell
                            key={cell.id}
                            cell={cell}
                            data={data}
                            dataProvider={dataProvider}
                            selectionManager={selectionManager}
                            scrollableRef={scrollableRef}
                            offsetLeft={cell.offsetLeft}
                        />
                    );
                })}
            </HStack>
        );
    };

    const rowClassName = cn(
        styles.row,
        stackClasses,
        byHover ? styles.rowHighlightedByHover : undefined,
        bySelect && isSelected ? styles.rowHighlightedBySelect : undefined,
        onClick ? styles.rowClickable : undefined
    );

    // Если высота строки фиксирована, то используем виртуализацию столбцов
    const centerVisibleCells = row.getCenterVisibleCells();
    const centerVirtualCells: VirtualizedCell<RowData>[] = useMemo(() => {
        if (strictHeight) {
            return virtualColumns.map(virtualColumn => {
                // Ячейка точно существует, так как виртуализация использует те же самые ячейки
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- см. выше
                const cell = centerVisibleCells[virtualColumn.index]!;
                return {
                    ...cell,
                    offsetLeft: virtualColumn.start,
                };
            });
        }

        return centerVisibleCells;
    }, [centerVisibleCells, strictHeight, virtualColumns]);

    const cells = (
        <>
            {grid.getIsSomeColumnsPinned('left') && (
                <HStack className={styles.pinnedLeftColumns}>{renderCells(row.getLeftVisibleCells())}</HStack>
            )}
            {Boolean(row.getCenterVisibleCells().length) && (
                <HStack className={styles.centerColumns}>{renderCells(centerVirtualCells)}</HStack>
            )}
            {/* Ставим фейковые строки в случае если нет центральных колонок, для того что бы появились строки по всей ширине грида */}
            {!row.getCenterVisibleCells().length && (
                <HStack className={cn(styles.rowHightlighter, hideBottomBorder ? null : styles.withBorder)} />
            )}
            {grid.getIsSomeColumnsPinned('right') && (
                <HStack className={styles.pinnedRightColumns}>{renderCells(row.getRightVisibleCells())}</HStack>
            )}
        </>
    );

    // коллбек клика, завязанный на данные строки
    const handledOnClick = useMemo(() => {
        if (!onClick || !data) return undefined;

        return () => onClick(data);
    }, [data, onClick]);

    const { pressProps } = usePress({ onPress: handledOnClick });

    // Если высота строки фиксирована, то нет необходимости использовать RowMeasurer
    if (strictHeight !== undefined) {
        return (
            <div
                className={rowClassName}
                style={inlineStyles}
                data-index={virtualRow.index}
                data-testid={`Grid.Row-${row.id}`}
                {...(handledOnClick ? pressProps : {})}
            >
                {cells}
            </div>
        );
    }

    return (
        <RowMeasurer
            className={rowClassName}
            measure={measure}
            shouldMeasure={!isRowLoading}
            style={inlineStyles}
            index={virtualRow.index}
            // передаём обработанный коллбек, внутри используем usePress
            onClick={handledOnClick}
            data-testid={`Grid.Row-${row.id}`}
        >
            {cells}
        </RowMeasurer>
    );
}
